import { Injectable, inject } from '@angular/core';
import { ButtonItem, SelectItem } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  SetReportManureSubstancesView,
  ToggleReportSortDirection,
  ToggleReportSortOption,
} from 'src/app/store/actions';
import { getReportManureSubstancesView, getReportSort } from 'src/app/store/selectors';

import {
  ReportManureSubstancesView,
  ReportSelectAction,
  ReportSortButtonAction,
  ReportSorterRequest,
  ReportSortOption,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ReportSortService {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);

  reportSort$ = this.store.select(getReportSort);
  reportManureSubstancesView$ = this.store.select(getReportManureSubstancesView);

  sort<T>(request: ReportSorterRequest<T>): T[] {
    return [...request.elements].sort((a, b) => {
      let textA = '';
      let textB = '';
      switch (request.option) {
        case ReportSortOption.sortCropFieldName:
          textA = `${request.cropFieldNameFunc(a)} ${request.cropNameFunc(a)}`;
          textB = `${request.cropFieldNameFunc(b)} ${request.cropNameFunc(b)}}`;
          break;
        case ReportSortOption.sortCropName:
          textA = `${request.cropNameFunc(a)} ${request.cropFieldNameFunc(a)}`;
          textB = `${request.cropNameFunc(b)} ${request.cropFieldNameFunc(b)}`;
          break;
      }
      const lesser = request.descending ? 1 : -1;
      const greater = request.descending ? -1 : 1;
      return textA.toLowerCase() < textB.toLowerCase() ? lesser : greater;
    });
  }

  getButtonItems(): Observable<ButtonItem[]> {
    return this.reportSort$.pipe(
      map((s) => [
        {
          icon: 'sort',
          viewValue: this.locale.translate(s.option),
          key: ReportSortButtonAction.sortOptions,
        },
        {
          icon: s.descending ? 'keyboard_arrow_down' : 'keyboard_arrow_up',
          viewValue: '',
          key: ReportSortButtonAction.sortDirection,
        },
      ]),
    );
  }

  getSelectItems(): Observable<SelectItem<ReportManureSubstancesView>[]> {
    return this.reportManureSubstancesView$.pipe(
      map((reportManureSubstancesView) => [
        {
          icon: 'table_rows',
          viewValue: this.locale.translate('manureSubstances'),
          key: ReportSelectAction.manureSubstancesView,
          value: reportManureSubstancesView,
          items: [
            {
              value: ReportManureSubstancesView.manureType,
              viewValue: this.locale.translate(ReportManureSubstancesView.manureType),
            },
            {
              value: ReportManureSubstancesView.period,
              viewValue: this.locale.translate(ReportManureSubstancesView.period),
            },
            {
              value: ReportManureSubstancesView.manureDetails,
              viewValue: this.locale.translate(ReportManureSubstancesView.manureDetails),
            },
          ],
        },
      ]),
    );
  }

  handleButtonAction(data: string) {
    switch (data) {
      case ReportSortButtonAction.sortOptions:
        this.store.dispatch(ToggleReportSortOption());
        break;
      case ReportSortButtonAction.sortDirection:
        this.store.dispatch(ToggleReportSortDirection());
        break;
    }
  }

  handleSelectAction([action, value]: [string, ReportManureSubstancesView]) {
    switch (action) {
      case ReportSelectAction.manureSubstancesView:
        this.store.dispatch(SetReportManureSubstancesView({ reportManureSubstancesView: value }));
        break;
    }
  }
}
